/**
 * Calendar utility functions for date formatting and availability calculations
 */

/**
 * Get Monday of the week for a given date
 * @param {Date} date - The date to get the week start for
 * @returns {Date} - Monday of the week
 */
export function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
}

/**
 * Format a date object to YYYY-MM-DD string
 * @param {Date} date - The date to format
 * @returns {string} - Formatted date string
 */
export function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format hour to 12-hour time string
 * @param {number} hour - Hour in 24-hour format (0-23)
 * @returns {string} - Formatted time string (e.g., "9:00 AM")
 */
export function formatTime(hour) {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:00 ${period}`;
}

/**
 * Get all appointments for a specific time slot
 * @param {Array} appointments - Array of all appointments
 * @param {Date} date - The date to check
 * @param {number} hour - The hour to check (0-23)
 * @returns {Array} - Array of appointments in that slot
 */
export function getAppointmentsForSlot(appointments, date, hour) {
  const dateStr = formatDate(date);
  
  return appointments.filter(apt => {
    if (!apt.start_time) return false;
    const aptDate = apt.start_time?.split('T')[0];
    if (aptDate !== dateStr) return false;
    // Extract hour from start_time and check if it matches
    const aptTime = apt.start_time?.split('T')[1];
    if (!aptTime) return false;
    const aptHour = parseInt(aptTime.split(':')[0]);
    return aptHour === hour;
  });
}

/**
 * Get availability for a specific time slot
 * Returns only CALENDAR-level availability (not server availability)
 * @param {Array} availability - Array of all availability slots
 * @param {Date} date - The date to check
 * @param {number} hour - The hour to check (0-23)
 * @returns {Object|null} - Availability object or null if not available
 */
export function getAvailabilityForSlot(availability, date, hour) {
  const dateStr = formatDate(date);
  const hourStr = `${hour.toString().padStart(2, '0')}:00`;
  
  // Only return CALENDAR-level availability (not server availability)
  // Calendar availability has calendar_id and NO server_id
  return availability.find(avail => 
    avail.date === dateStr && 
    avail.start_time <= hourStr && 
    avail.end_time > hourStr &&
    avail.is_available &&
    avail.calendar_id &&  // Must have calendar_id
    !avail.server_id      // Must NOT have server_id
  );
}

/**
 * Check if a time slot is bookable for multiple servers calendars
 * 
 * SLOT AVAILABILITY BUSINESS RULES (Jan 2026):
 * A time slot is NOT bookable if:
 *   1) Calendar admin does not offer service in the slot (no availability defined)
 *   2) The slot is on-hold
 *   3) Time slot is available but not for the service needed (checked at booking time)
 *   4) The slot is booked and there are no more servers available for the same service
 * 
 * IMPLEMENTATION NOTES:
 * - Rules 1, 2, 4 are checked here
 * - Rule 3 (service-specific availability) is validated at booking time in the backend
 * - For customers, bookedSlots comes from /api/appointments/slot-availability endpoint
 *   which returns ALL bookings (without customer details for privacy)
 * 
 * @param {Object} calendar - Calendar object
 * @param {Array} availability - Array of availability slots
 * @param {Array} appointments - Array of appointments (user's own)
 * @param {Date} day - The date to check
 * @param {number} hour - The hour to check (0-23)
 * @param {Object} slotAvailability - Pre-fetched availability for the slot
 * @param {Array} bookedSlots - Optional: ALL booked slots on calendar (for customers)
 * @param {Array} heldSlots - Optional: ALL held slots on calendar
 * @returns {Object} - { bookable: boolean, reason: string|null }
 */
export function isSlotBookableMultipleServers(calendar, availability, appointments, day, hour, slotAvailability, bookedSlots = null, heldSlots = null, isCustomer = false, services = []) {
  const dateStr = formatDate(day);
  const timeStr = `${hour.toString().padStart(2, '0')}:00`;

  // === BOOKING RULES VALIDATION (for customers only) ===
  if (isCustomer && calendar) {
    const slotDateTime = new Date(day);
    slotDateTime.setHours(hour, 0, 0, 0);
    const now = new Date();
    
    // Rule 1: Min Advance Booking Hours
    const minAdvanceHours = calendar.min_advance_booking_hours || 1;
    const minBookingTime = new Date(now.getTime() + minAdvanceHours * 60 * 60 * 1000);
    if (slotDateTime < minBookingTime) {
      return { bookable: false, reason: 'too_soon', minHours: minAdvanceHours };
    }
    
    // Rule 2: Max Advance Booking Days
    const maxAdvanceDays = calendar.max_advance_booking_days || 30;
    const maxBookingTime = new Date(now.getTime() + maxAdvanceDays * 24 * 60 * 60 * 1000);
    if (slotDateTime > maxBookingTime) {
      return { bookable: false, reason: 'too_far', maxDays: maxAdvanceDays };
    }
  }

  // RULE 2: Check if slot is on-hold
  if (heldSlots && heldSlots.length > 0) {
    const isHeld = heldSlots.some(slot => slot.date === dateStr && slot.time === timeStr);
    if (isHeld) {
      return { bookable: false, reason: 'slot_on_hold' };
    }
  }

  if (!calendar?.multiple_servers) {
    // Non-Multiple Servers mode
    
    // RULE 1: Check if slot has availability (admin offers service)
    if (!slotAvailability) {
      return { bookable: false, reason: 'no_availability' };
    }
    
    // RULE: Check if ANY booking exists for this slot
    // Once a slot has ANY booking, it's no longer bookable
    // (Admin can edit the existing booking to add more customers for multi-customer services)
    const slotsToCheck = bookedSlots || appointments;
    const isBooked = slotsToCheck.some(slot => {
      const slotDate = slot.date || slot.start_time?.split('T')[0];
      const slotTime = slot.time || slot.start_time?.split('T')[1]?.substring(0, 5);
      return slotDate === dateStr && slotTime === timeStr;
    });
    
    if (isBooked) {
      return { bookable: false, reason: 'slot_booked' };
    }
    
    return { bookable: true, reason: null };
  }

  // Multiple Servers mode
  
  // RULE 1: Check if slot has availability (admin offers service)
  if (!slotAvailability) {
    return { bookable: false, reason: 'no_availability' };
  }

  // Get all servers assigned to this calendar
  const calendarServers = calendar.server_ids || [];
  
  if (calendarServers.length === 0) {
    // No servers assigned
    return { bookable: false, reason: 'no_servers' };
  }

  // Check how many servers are working at this time slot
  const workingServers = availability.filter(avail => {
    if (avail.date !== dateStr || !avail.server_id || !calendarServers.includes(avail.server_id)) {
      return false;
    }
    
    // Parse server's working hours
    const serverStartHour = parseInt(avail.start_time.split(':')[0]);
    const serverEndHour = parseInt(avail.end_time.split(':')[0]);
    
    // Check if this hour falls within server's working hours
    return hour >= serverStartHour && hour < serverEndHour;
  });

  if (workingServers.length === 0) {
    // No servers working during this time
    return { bookable: false, reason: 'no_servers_working' };
  }

  // Check how many of the working servers are already booked
  // Use bookedSlots if provided (for customers), otherwise use appointments
  const workingServerIds = workingServers.map(ws => ws.server_id);
  const slotsToCheck = bookedSlots || appointments;
  
  const bookedServerIds = slotsToCheck
    .filter(slot => {
      const slotDate = slot.date || slot.start_time?.split('T')[0];
      const slotTime = slot.time || slot.start_time?.split('T')[1]?.substring(0, 5);
      return slotDate === dateStr && slotTime === timeStr && slot.server_id && workingServerIds.includes(slot.server_id);
    })
    .map(slot => slot.server_id);

  // At least one working server must be available (not booked)
  const availableServers = workingServerIds.filter(id => !bookedServerIds.includes(id));
  
  if (availableServers.length === 0) {
    // All working servers are fully booked
    return { bookable: false, reason: 'all_servers_busy' };
  }

  // At least one server is available
  return { bookable: true, reason: null };
}

/**
 * Check if a date is today
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is today
 */
export function isToday(date) {
  const today = new Date();
  return date.getDate() === today.getDate() &&
         date.getMonth() === today.getMonth() &&
         date.getFullYear() === today.getFullYear();
}

/**
 * Calculate the nearest 15-minute interval based on Y-coordinate within an hourly cell
 * @param {number} relativeY - Y position relative to cell (0-1, where 0 is top, 1 is bottom)
 * @param {number} hour - The hour of the cell (0-23)
 * @returns {string} - Time string in HH:MM format
 */
export function calculateFloatingStartTime(relativeY, hour) {
  // Calculate minutes based on Y position
  // relativeY: 0 -> 0 min, 0.25 -> 15 min, 0.5 -> 30 min, 0.75 -> 45 min
  const minutes = Math.floor(relativeY * 4) * 15;
  const clampedMinutes = Math.min(45, minutes); // Max 45 minutes
  
  return `${hour.toString().padStart(2, '0')}:${clampedMinutes.toString().padStart(2, '0')}`;
}

/**
 * Get continuous availability blocks for a day (for floating calendars)
 * @param {Array} availability - Array of availability slots
 * @param {Date} date - The date to check
 * @returns {Array} - Array of continuous blocks { startHour, endHour }
 */
export function getContinuousAvailabilityBlocks(availability, date) {
  const dateStr = formatDate(date);
  
  // Get all calendar-level availability for this date
  const dayAvailability = availability.filter(avail => 
    avail.date === dateStr && 
    avail.is_available &&
    avail.calendar_id &&
    !avail.server_id
  );
  
  if (dayAvailability.length === 0) {
    return [];
  }
  
  // Convert to hour ranges and merge overlapping/adjacent blocks
  const blocks = [];
  
  dayAvailability.forEach(avail => {
    const startHour = parseInt(avail.start_time.split(':')[0]);
    const endHour = parseInt(avail.end_time.split(':')[0]);
    
    // Check if this block overlaps or is adjacent to an existing block
    let merged = false;
    for (let i = 0; i < blocks.length; i++) {
      if (startHour <= blocks[i].endHour && endHour >= blocks[i].startHour) {
        // Merge with existing block
        blocks[i].startHour = Math.min(blocks[i].startHour, startHour);
        blocks[i].endHour = Math.max(blocks[i].endHour, endHour);
        merged = true;
        break;
      }
    }
    
    if (!merged) {
      blocks.push({ startHour, endHour });
    }
  });
  
  return blocks;
}
